import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { REMOTE_SUPPORT_URL } from "@/data/content";

const LINKS = [
  { label: "Services", href: "#services", testid: "nav-link-services" },
  { label: "Products", href: "#products", testid: "nav-link-products" },
  { label: "Contact", href: "#contact", testid: "nav-link-contact" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-black/60 backdrop-blur-xl"
      data-testid="main-nav"
    >
      <div className="mx-auto flex max-w-[1440px] items-center justify-between px-6 py-4 md:px-12">
        <a href="#top" className="flex items-center" data-testid="nav-logo">
          <img
            src="/opus-logo.png"
            alt="Opus Consulting, LLC"
            className="h-12 w-auto brightness-[1.7] saturate-[1.1]"
          />
        </a>

        <nav className="hidden items-center gap-10 md:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testid}
              className="group relative font-mono text-xs uppercase tracking-[0.25em] text-[#A0A0A0] transition-colors duration-300 hover:text-white"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#0033FF] transition-[width] duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <a
          href={REMOTE_SUPPORT_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="nav-remote-support-btn"
          className="group flex items-center gap-2 bg-white px-5 py-2.5 font-mono text-xs font-medium uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-[#0033FF] hover:text-white"
        >
          Remote Support
          <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </a>

        <button
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-btn"
          className="flex h-10 w-10 items-center justify-center border border-white/20 text-white transition-colors duration-300 hover:border-[#0033FF] md:hidden"
        >
          {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-white/10 bg-black/90 backdrop-blur-xl md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col px-6 py-2">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-${l.testid}`}
                  className="border-b border-white/10 py-4 font-mono text-sm uppercase tracking-[0.25em] text-[#A0A0A0] transition-colors duration-300 last:border-b-0 hover:text-white"
                >
                  {l.label}
                </a>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
